import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo.svg';
import { User, Lock, Mail, Eye, EyeOff, Building, Phone, UserCircle } from 'lucide-react';

export default function SignupForm({ onSwitchToLogin }) {
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    mobile: '',
    company: '',
    role: 'Property Manager',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const roles = [
    'Property Manager',
    'Retail Manager', 
    'Asset Manager',
    'Technician',
    'Maintenance Staff',
    'Service Provider'
  ];

  // Technician-specific fields
  const [technicianFields, setTechnicianFields] = useState({
    specialization: '',
    certifications: '',
    availability: 'Full-time',
    serviceArea: '',
    experience: ''
  });

  const availabilityOptions = ['Full-time', 'Part-time', 'Contract', 'On-call'];
  const specializationOptions = [
    'HVAC Systems',
    'Electrical',
    'Plumbing',
    'General Maintenance',
    'Security Systems',
    'Cleaning Services',
    'Landscaping',
    'Fire Safety',
    'Other'
  ];

  // Check if current role requires technician fields
  const isTechnicianRole = ['Technician', 'Maintenance Staff', 'Service Provider'].includes(formData.role);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTechnicianFieldChange = (e) => {
    setTechnicianFields({
      ...technicianFields,
      [e.target.name]: e.target.value
    });
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    // Check required fields
    const requiredFields = ['name', 'surname', 'email', 'mobile', 'company', 'password'];
    for (const field of requiredFields) {
      if (!formData[field].trim()) {
        setError(`${field.charAt(0).toUpperCase() + field.slice(1)} is required`);
        return false;
      }
    }

    // Validate technician-specific fields if applicable
    if (isTechnicianRole) {
      if (!technicianFields.specialization.trim()) {
        setError('Specialization is required for technician roles');
        return false;
      }
      if (!technicianFields.serviceArea.trim()) {
        setError('Service area is required for technician roles');
        return false;
      }
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    // Validate mobile
    if (formData.mobile.length < 10) {
      setError('Mobile number must be at least 10 digits');
      return false;
    }

    // Validate password
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      const userData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        mobile: formData.mobile,
        company: formData.company,
        role: formData.role,
        password: formData.password,
        // Include technician fields if applicable
        ...(isTechnicianRole && {
          specialization: technicianFields.specialization,
          certifications: technicianFields.certifications,
          availability: technicianFields.availability,
          serviceArea: technicianFields.serviceArea,
          experience: technicianFields.experience
        })
      };

      const result = await signup(userData);
      
      if (result.success) {
        setSuccess('Account created successfully! You can now sign in.');
        setFormData({
          name: '',
          surname: '',
          email: '',
          mobile: '',
          company: '',
          role: 'Property Manager',
          password: '',
          confirmPassword: ''
        });
        // Auto-switch to login after 2 seconds
        setTimeout(() => {
          onSwitchToLogin();
        }, 2000);
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#EDF0F1'}}>
      <div className="max-w-lg w-full space-y-8">
        <div className="text-center">
          <img 
            src={logo} 
            alt="Fortress Asset Managers Logo" 
            className="mx-auto h-16 w-16"
          />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Join Fortress Asset Managers
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Name and Surname */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      placeholder="First name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <UserCircle className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      id="surname"
                      name="surname"
                      type="text"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      placeholder="Last name"
                      value={formData.surname}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#00C5CB'}}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Mobile and Company */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                    Mobile Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      id="mobile"
                      name="mobile"
                      type="tel"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      placeholder="Mobile number"
                      value={formData.mobile}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      id="company"
                      name="company"
                      type="text"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      placeholder="Company name"
                      value={formData.company}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{'--tw-ring-color': '#00C5CB'}}
                  onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                  value={formData.role}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {roles.map(role => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>

              {/* Technician-specific fields */}
              {isTechnicianRole && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Technician Information</h3>
                  
                  {/* Specialization */}
                  <div>
                    <label htmlFor="specialization" className="block text-sm font-medium text-gray-700 mb-1">
                      Specialization *
                    </label>
                    <select
                      id="specialization"
                      name="specialization"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      value={technicianFields.specialization}
                      onChange={handleTechnicianFieldChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Select specialization</option>
                      {specializationOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Service Area and Experience */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="serviceArea" className="block text-sm font-medium text-gray-700 mb-1">
                        Service Area *
                      </label>
                      <input
                        id="serviceArea"
                        name="serviceArea"
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{'--tw-ring-color': '#00C5CB'}}
                        onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                        placeholder="e.g., Downtown, North Side"
                        value={technicianFields.serviceArea}
                        onChange={handleTechnicianFieldChange}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                        Years of Experience
                      </label>
                      <input
                        id="experience"
                        name="experience"
                        type="number"
                        min="0"
                        max="50"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                        style={{'--tw-ring-color': '#00C5CB'}}
                        onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                        placeholder="Years"
                        value={technicianFields.experience}
                        onChange={handleTechnicianFieldChange}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  {/* Availability */}
                  <div>
                    <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
                      Availability
                    </label>
                    <select
                      id="availability"
                      name="availability"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      value={technicianFields.availability}
                      onChange={handleTechnicianFieldChange}
                      disabled={isSubmitting}
                    >
                      {availabilityOptions.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Certifications */}
                  <div>
                    <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-1">
                      Certifications
                    </label>
                    <textarea
                      id="certifications"
                      name="certifications"
                      rows="3"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#00C5CB'}}
                      onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                      placeholder="List your relevant certifications (optional)"
                      value={technicianFields.certifications}
                      onChange={handleTechnicianFieldChange}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              )}

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#00C5CB'}}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                    style={{'--tw-ring-color': '#00C5CB'}}
                    onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity"
                style={{backgroundColor: '#00C5CB', '--tw-ring-color': '#00C5CB'}}
                onFocus={(e) => e.target.style.setProperty('--tw-ring-color', '#00C5CB')}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-sm font-medium hover:underline"
                style={{color: '#00C5CB'}}
                disabled={isSubmitting}
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}